import { apiClient } from "@/lib/api/http"
import type { Company } from "@/lib/api/company.api"

// ── Types ───────────────────────────────────────────────────────────────────────
export interface SellerProfile {
  id: string
  userId: string
  companyId: string | null
  designation: string
  department: string
  phoneNumber: string
  bio: string
  linkedinUrl?: string
  secondaryPhoneNumber?: string
  yearsOfExperience?: number
  employeeRole: string
  isActive: boolean
  verificationStatus: string
  createdAt: string
  updatedAt: string
  company: Company | null
}

export interface CreateSellerProfilePayload {
  designation: string
  department: string
  phoneNumber: string
  bio: string
}

// ── Envelope wrapper (backend wraps all GET responses) ──────────────────────────
interface Envelope<T> {
  success: boolean
  statusCode: number
  message: string
  data: T
}

// ── GET /seller/{id} — profile completeness check (login flow) ──────────────────
// Returns null when SELLER_PROFILE_INCOMPLETE, throws everything else
export async function getSellerProfile(
  id: string,
  token: string
): Promise<SellerProfile | null> {
  try {
    const { data: envelope } = await apiClient.get<Envelope<SellerProfile>>(
      `/seller/${id}`,
      { headers: { Authorization: `Bearer ${token}` } }
    )
    return envelope.data
  } catch (error: unknown) {
    const errCode =
      (error as { code?: string })?.code ??
      (error as { errorCode?: string })?.errorCode

    if (errCode === "SELLER_PROFILE_INCOMPLETE") return null
    throw error
  }
}

// ── GET /seller/{id} — full profile for display in dashboard ────────────────────
export async function getSellerProfileById(
  id: string,
  token: string
): Promise<SellerProfile> {
  const { data: envelope } = await apiClient.get<Envelope<SellerProfile>>(
    `/seller/${id}`,
    { headers: { Authorization: `Bearer ${token}` } }
  )
  return envelope.data
}

// ── POST /seller ────────────────────────────────────────────────────────────────
export async function createSellerProfile(
  payload: CreateSellerProfilePayload,
  token: string
): Promise<SellerProfile> {
  const { data: envelope } = await apiClient.post<any>(
    "/seller",
    payload,
    { headers: { Authorization: `Bearer ${token}` } }
  )
  return envelope?.data ?? envelope
}

// ── PATCH /seller/{id} ──────────────────────────────────────────────────────────
export interface UpdateSellerProfilePayload {
  designation?: string
  department?: string
  phoneNumber?: string
  bio?: string
  linkedinUrl?: string
  secondaryPhoneNumber?: string
  yearsOfExperience?: number
}

export async function updateSellerProfile(
  id: string,
  payload: UpdateSellerProfilePayload,
  token: string
): Promise<SellerProfile> {
  const { data: envelope } = await apiClient.patch<Envelope<SellerProfile>>(
    `/seller/${id}`,
    payload,
    { headers: { Authorization: `Bearer ${token}` } }
  )
  return envelope.data
}
